import useDeleteWorkflow from "api/api-hooks/workflows/useDeleteWorkflow";
import useWorkflows from "api/api-hooks/workflows/useWorkflows";
import { tenantIDAtom } from "atoms/GlobalAtoms";
import AppPageHeader from "components/Headers/AppPageHeader";
import AppEmptyState from "elements/AppEmptyState";
import AppTooltip from "elements/AppTooltip";
import SkeletonLoader from "elements/SkeletonLoader";
import { useAtomValue } from "jotai";
import { Container } from "reactstrap";
import { newToastEvent } from "utils/ToastUtil";
import ItemCardList from "components/Modules/ItemCardList";
import { Fragment } from "react";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { useNavigate } from "react-router-dom";

// Initial Triggers - File Upload (CSV, JSON, Excel)

/**
 * WorkflowHomeView - Home page for Workflows, Allows you to view cloud workflows,
 *                    provides links for editing and creating new workflows.
 */
const WorkflowHomeView = () => {
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom);
  const title = "Workflows";
  const description = "Create and manage your cloud workflows";
  const { workflows, workflowsLoading } = useWorkflows();
  const {
    deleteWorkflow: deleteW,
    deletingWorkflow,
    deleteWorkflowError,
  } = useDeleteWorkflow();

  const deleteWorkflow = (workflowID) => {
    deleteW({
      workflowID: workflowID,
      onSuccess: () => {
        newToastEvent("success", "Successfully deleted workflow.");
      },
    });
  };

  const openNewWorkflow = () =>
    navigate(`/clouds/${tenantID}/workflows/edit/new`);

  return (
    <>
      <AppHelmet title="Workflows - GrowthCloud.com" />
      <Container fluid className="mt-4">
        <AppPageHeader
          title={title}
          description={description}
          action={() => openNewWorkflow()}
          actionButtonName="Create"
          size=""
        />

        {!workflowsLoading && workflows?.length > 0 ? (
          <WorkflowsList
            workflows={workflows}
            onOpen={(id) =>
              navigate(`/clouds/${tenantID}/workflows/edit/${id}`)
            }
            onDelete={(e, id) => {
              e.stopPropagation();
              deleteWorkflow(id);
            }}
            deleting={deletingWorkflow}
          />
        ) : workflowsLoading ? (
          <>
            <SkeletonLoader />
            <SkeletonLoader />
            <SkeletonLoader />
          </>
        ) : (
          <AppEmptyState
            title="No Workflows"
            description="Get started by creating your first cloud workflow"
            buttonTitle="Create"
            onClick={openNewWorkflow}
            icon="mdi-sitemap"
          />
        )}
      </Container>
    </>
  );
};

function WorkflowsList({ workflows, onOpen, onDelete, deleting }) {
  return workflows.map((wf, i) => {
    if (wf?.HasError) {
      return (
        <Fragment key={wf._id}>
          <AppTooltip
            content="This workflow has configuration errors. Please fix these errors to be able to use this workflow."
            useTooltip
          >
            <ItemCardList
              removable
              editable
              removeStatus={deleting}
              item={{
                Name: wf.Name,
                APIName: wf._id,
                Description: wf.Description,
              }}
              removeAction={(e) => onDelete(e, wf._id)}
              icon={wf.Icon ? wf.Icon : "mdi-sitemap"}
              editableAction={() => onOpen(wf._id)}
              hasError={wf?.HasError}
              yMargin={1}
            />
          </AppTooltip>
          {i !== workflows?.length - 1 && <hr className="my-0"></hr>}
        </Fragment>
      );
    }

    return (
      <Fragment key={wf._id}>
        <ItemCardList
          removable
          editable
          removeStatus={deleting}
          item={{ Name: wf.Name, APIName: wf._id, Description: wf.Description }}
          removeAction={(e) => onDelete(e, wf._id)}
          icon={wf.Icon ? wf.Icon : "mdi-sitemap"}
          editableAction={() => onOpen(wf._id)}
          description={wf.Description}
          yMargin={1}
        />
        {i !== workflows?.length - 1 && <hr className="my-0"></hr>}
      </Fragment>
    );
  });
}

export default WorkflowHomeView;
